// @flow
import { doesArrayContainAll } from '@src/common-utils/utils';

import {
  convertDimsKeyToDimArray,
  getAggregatedUsageForKafkaMetricBasedOnGuidance,
  getGuidanceForMetric,
  getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys,
  getUsagesForMetric,
  isFirstDimArraySubSetOfSecond,
} from './utils';

// ═══════════════════════════════════════════════════════════════════════════════
// Usage Calculator Functions
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Calculates usage for non-Kafka single-select metrics.
 * Iterates through all usage keys and sums values where keys match the dimension combination.
 *
 * @example
 * // Single dimension value
 * getUsageForSingleSelectAndNonKafkaMetrics(mldGuidance, ["aws"], "FlinkPoolNumCFUs")
 * // Returns: 125.5
 *
 * @example
 * // "all" in dimension - sums all usages
 * getUsageForSingleSelectAndNonKafkaMetrics(mldGuidance, ["all"], "FlinkPoolNumCFUs")
 * // Returns: 212.8 (125.5 + 87.3)
 *
 * @example
 * // Multiple dimension values - sums matching usages
 * getUsageForSingleSelectAndNonKafkaMetrics(mldGuidance, ["aws", "gcp"], "FlinkPoolNumCFUs")
 * // Returns: 212.8 (125.5 + 87.3)
 *
 * @param {Object} mldGuidance - MLD guidance object containing usages
 * @param {string[]} dimCombinationArray - Dimension combination array
 * @param {string} metricName - The metric name
 * @returns {number} The usage value, or 0 if not found
 */
export const getUsageForSingleSelectAndNonKafkaMetrics = (
  mldGuidance,
  dimCombinationArray,
  metricName
) => {
  const usagesForDimsSentByBE = getUsagesForMetric(metricName, mldGuidance);
  let totalUsage = 0;

  // If no usages are present for the given metric, return 0
  if (!usagesForDimsSentByBE) {
    return totalUsage;
  }

  // Iterate through all usage keys and check if they match the dimension combination
  for (const [key, usage] of Object.entries(usagesForDimsSentByBE)) {
    const keyDimArray = convertDimsKeyToDimArray(key);
    if (isFirstDimArraySubSetOfSecond(keyDimArray, dimCombinationArray)) {
      totalUsage += usage;
    }
  }

  return totalUsage;
};

/**
 * Calculates usage for Kafka metrics.
 * Returns 'N/A' if any selected dimension contains 'all' or if selected dimension combination isn't in guidance.
 * Otherwise, returns the pre-computed aggregated usage value for all usage keys present in guidance.
 *
 * @param {Object} mldGuidance - MLD guidance object containing guidance and usage data.
 * @param {string[]} selectedDimsArray - User selected dimension values.
 * @param {string} metricName - The Kafka metric name.
 * @returns {number|string} Aggregated usage value or 'N/A'.
 */
export const getUsageForSingleSelectAndKafkaMetrics = (
  mldGuidance,
  selectedDimsArray,
  metricName
) => {
  // 'all' in selected dims → N/A
  if (doesArrayContainAll(selectedDimsArray)) {
    return 'N/A';
  }

  const guidanceData = getGuidanceForMetric(metricName, mldGuidance);
  const guidanceKeys = Object.keys(guidanceData);

  // Selected dims not in guidance → N/A
  if (!getIfGivenDimsArrayIsSubSetOfAnyKeyInTheGivenSetOfKeys(selectedDimsArray, guidanceKeys)) {
    return 'N/A';
  }

  return getAggregatedUsageForKafkaMetricBasedOnGuidance(metricName, mldGuidance);
};

/**
 * Gets usage values for multi-select dimension combinations.
 * Validates that only the last column can be an array, then breaks down
 * the combination into individual dimension arrays and gets usage for each.
 *
 * @example
 * // Input: mldGuidance, ['A', 'B', 'C', ['D1', 'D2', 'D3']], 'ConnectNumTasks'
 * // Creates combinations: ['A', 'B', 'C', 'D1'], ['A', 'B', 'C', 'D2'], ['A', 'B', 'C', 'D3']
 * // Returns: { D1: 12.23, D2: 23.45, D3: 34.56 }
 *
 * @param {Object} mldGuidance - MLD guidance object containing usages
 * @param {(string|string[])[]} dimCombinationArray - Dimension combination array where only last element can be an array
 * @param {string} metricName - The metric name
 * @returns {Object<string, number>} Object with last dimension values as keys and usage values as numbers
 * @throws {Error} If validation fails (arrays in non-last positions, empty array, etc.)
 */
export const getMultiSelectUsageForDimensionCombination = (
  mldGuidance,
  dimCombinationArray,
  metricName
) => {
  // Validate input
  if (!dimCombinationArray || dimCombinationArray.length === 0) {
    throw new Error('Dimension combination array cannot be empty');
  }

  const usagesForDimsSentByBE = getUsagesForMetric(metricName, mldGuidance);
  if (!usagesForDimsSentByBE) {
    return {};
  }

  const lastIndex = dimCombinationArray.length - 1;
  const lastElement = dimCombinationArray[lastIndex];

  // Validate that only the last element can be an array
  for (let i = 0; i < dimCombinationArray.length - 1; i++) {
    if (Array.isArray(dimCombinationArray[i])) {
      throw new Error(
        `Only the last column can be an array. Found array at index ${i}: ${JSON.stringify(
          dimCombinationArray[i]
        )}`
      );
    }
  }

  // Validate that the last element is indeed an array
  if (!Array.isArray(lastElement)) {
    throw new Error(
      `Last column must be an array. Found: ${typeof lastElement} at index ${lastIndex}`
    );
  }

  // Validate that the last element array is not empty
  if (lastElement.length === 0) {
    throw new Error('Last column array cannot be empty');
  }

  // Extract base dimensions (all except the last)
  const baseDimensions = dimCombinationArray.slice(0, lastIndex);

  // Build result object
  const result = {};

  // For each value in the last dimension array, create a combination and get usage
  for (const lastDimValue of lastElement) {
    // Validate that lastDimValue is not an array (nested arrays not allowed)
    if (Array.isArray(lastDimValue)) {
      throw new Error(
        `Nested arrays are not allowed. Found array in last column: ${JSON.stringify(lastDimValue)}`
      );
    }

    // Create the full dimension combination: baseDimensions + lastDimValue
    const fullCombination = [...baseDimensions, lastDimValue];

    // Get usage for this combination
    result[lastDimValue] = getUsageForSingleSelectAndNonKafkaMetrics(
      mldGuidance,
      fullCombination,
      metricName
    );
  }

  return result;
};
